import clsx from 'clsx';
import { ChevronRight } from '@/components/ui/icons';
import { useState, type ReactNode } from 'react';

export interface DragItem {
  type: 'space' | 'folder' | 'list';
  id: string;
}
export type DropPos = 'before' | 'after' | 'inside';

export interface SidebarDnd {
  item: DragItem;
  /** drop positions this row accepts for the dragged item */
  accepts: (dragged: DragItem) => DropPos[];
  onDrop: (dragged: DragItem, pos: DropPos) => void;
}

// HTML5 drag data can't be read during dragover, so the active item lives here.
let dragging: DragItem | null = null;

export function SidebarItem({
  icon,
  label,
  count,
  selected,
  depth = 0,
  onClick,
  onContextMenu,
  actions,
  expandable,
  expanded,
  onToggle,
  badge,
  muted,
  title,
  dnd,
}: {
  icon?: ReactNode;
  label: ReactNode;
  count?: number | string;
  selected?: boolean;
  depth?: number;
  onClick?: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  actions?: ReactNode;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  badge?: ReactNode;
  muted?: boolean;
  title?: string;
  dnd?: SidebarDnd;
}) {
  const [dropPos, setDropPos] = useState<DropPos | null>(null);
  const posFor = (e: React.DragEvent): DropPos | null => {
    if (!dnd || !dragging || (dragging.type === dnd.item.type && dragging.id === dnd.item.id)) return null;
    const ok = dnd.accepts(dragging);
    if (!ok.length) return null;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    const want: DropPos = ok.includes('inside') && (!ok.includes('before') || (y > 0.28 && y < 0.72)) ? 'inside' : y < 0.5 ? 'before' : 'after';
    return ok.includes(want) ? want : ok[0];
  };
  return (
    <div
      className={clsx('sb-item', { selected, muted }, dropPos && `drop-${dropPos}`)}
      draggable={!!dnd}
      onDragStart={
        dnd
          ? (e) => {
              dragging = dnd.item;
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', `${dnd.item.type}:${dnd.item.id}`);
            }
          : undefined
      }
      onDragEnd={() => {
        dragging = null;
        setDropPos(null);
      }}
      onDragOver={(e) => {
        const pos = posFor(e);
        if (!pos) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (pos !== dropPos) setDropPos(pos);
      }}
      onDragLeave={() => setDropPos(null)}
      onDrop={(e) => {
        const pos = posFor(e);
        setDropPos(null);
        if (!pos || !dragging || !dnd) return;
        e.preventDefault();
        dnd.onDrop(dragging, pos);
        dragging = null;
      }}
      style={{ paddingLeft: `calc(${10 + depth * 14}px + var(--sb-indent, 0px))` }}
      onClick={onClick}
      onContextMenu={onContextMenu}
      title={title}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      <span
        className={clsx('sb-item-icon', { expandable })}
        onClick={
          expandable
            ? (e) => {
                e.stopPropagation();
                onToggle?.();
              }
            : undefined
        }
      >
        {expandable ? (
          <>
            <span className="sb-item-icon-main">{icon}</span>
            <ChevronRight size={14} className={clsx('sb-item-chevron', { open: expanded })} />
          </>
        ) : (
          icon
        )}
      </span>
      <span className="sb-item-label truncate">{label}</span>
      {badge}
      {count !== undefined && count !== 0 && <span className="sb-item-count">{count}</span>}
      {actions && (
        <span className="sb-item-actions" onClick={(e) => e.stopPropagation()}>
          {actions}
        </span>
      )}
    </div>
  );
}

export function SidebarSection({
  title,
  icon,
  open,
  onToggle,
  actions,
  children,
}: {
  title: string;
  icon?: ReactNode;
  open: boolean;
  onToggle: () => void;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="sb-section">
      <div className="sb-section-head" onClick={onToggle} role="button">
        {icon && <span className="sb-section-icon">{icon}</span>}
        <span className="sb-section-title">{title}</span>
        <ChevronRight size={12} className={clsx('sb-section-chevron', { open })} />
        <span className="spacer" />
        {actions && (
          <span className="sb-section-actions" onClick={(e) => e.stopPropagation()}>
            {actions}
          </span>
        )}
      </div>
      {open && <div className="sb-section-body">{children}</div>}
    </div>
  );
}
